import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  INCLUSION_STATUSES,
  VENUE_INCLUSION_KEYS,
  extrasCents,
  type InclusionStatus,
  type VenueInclusion,
} from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { TextInput } from '@/components/ui/Field';
import { money } from '@/lib/format';

/**
 * What the venue covers, and what the rest will cost you.
 *
 * The estimate is the point. A venue quoting less and including nothing is more
 * expensive than one quoting more and covering the DJ and the photographer, and
 * the only way to see that is to write down what the gaps cost.
 *
 * So every row that is not `included` asks for a number — both of the other
 * statuses mean you are paying someone, and the difference between them is who
 * invoices you rather than whether it costs anything.
 *
 * Unanswered categories are shown but hold no status, because "we have not asked
 * them yet" and "they do not offer it" are different facts and only one of them
 * belongs in the total.
 */

const STATUS_STYLE: Record<InclusionStatus, string> = {
  included: 'border-status-attendingFg bg-status-attendingBg text-status-attendingFg',
  extra: 'border-status-pendingFg bg-status-pendingBg text-status-pendingFg',
  not_offered: 'border-status-declinedFg bg-status-declinedBg text-status-declinedFg',
};

const centsFromInput = (text: string): number | undefined => {
  const cleaned = text.replace(/[^0-9.]/g, '');
  if (cleaned === '') return undefined;
  const value = Number(cleaned);
  return Number.isFinite(value) ? Math.round(value * 100) : undefined;
};

const inputFromCents = (cents: number | undefined): string =>
  cents === undefined ? '' : String(cents / 100);

export function InclusionsEditor({
  value,
  currency,
  onChange,
}: {
  value: VenueInclusion[];
  currency: string;
  onChange: (next: VenueInclusion[]) => void;
}) {
  const { t } = useTranslation();
  const [customLabel, setCustomLabel] = useState('');

  const byKey = useMemo(() => new Map(value.map((i) => [i.key, i])), [value]);

  // Catalog first, in catalog order, then anything custom already on the venue.
  const rows = useMemo(() => {
    const custom = value.filter((i) => !VENUE_INCLUSION_KEYS.includes(i.key as never));
    return [...VENUE_INCLUSION_KEYS.map((key) => ({ key: key as string })), ...custom];
  }, [value]);

  const set = (key: string, patch: Partial<VenueInclusion> | null, label?: string) => {
    if (patch === null) {
      onChange(value.filter((i) => i.key !== key));
      return;
    }
    const existing = byKey.get(key);
    if (!existing) {
      onChange([
        ...value,
        { key, status: 'included', ...(label ? { label } : {}), ...patch } as VenueInclusion,
      ]);
      return;
    }
    onChange(value.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  };

  const extras = extrasCents(value);

  const addCustom = () => {
    const label = customLabel.trim();
    if (!label) return;
    const key = `custom:${label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')}`;
    if (key === 'custom:' || byKey.has(key)) {
      setCustomLabel('');
      return;
    }
    onChange([...value, { key, label, status: 'included' }]);
    setCustomLabel('');
  };

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col divide-y divide-line">
        {rows.map((row) => {
          const current = byKey.get(row.key);
          const label =
            current?.label ??
            (row.key.startsWith('custom:')
              ? row.key.replace(/^custom:/, '').replace(/_/g, ' ')
              : t(`inclusions.${row.key}`));

          return (
            <li key={row.key} className="flex flex-wrap items-center gap-2 py-2">
              <span className="min-w-[9rem] flex-1 text-body">{label}</span>

              <span className="flex gap-1" role="group" aria-label={label}>
                {INCLUSION_STATUSES.map((status) => {
                  const on = current?.status === status;
                  return (
                    <button
                      key={status}
                      type="button"
                      aria-pressed={on}
                      // Pressing the status it already has clears the row, which
                      // is how you get back to "we have not asked".
                      onClick={() => set(row.key, on ? null : { status }, label)}
                      className={`min-h-tap rounded-pill border px-3 text-caption transition-colors ${
                        on
                          ? STATUS_STYLE[status]
                          : 'border-line bg-paper text-leaf-soft hover:border-leaf-soft hover:text-leaf'
                      }`}
                    >
                      {t(`inclusions.status.${status}`)}
                    </button>
                  );
                })}
              </span>

              {/* Anything the venue does not cover costs something, whoever
                  ends up invoicing for it. */}
              {current !== undefined && current.status !== 'included' ? (
                <label className="w-32">
                  <span className="sr-only">{t('inclusions.estimate', { name: label })}</span>
                  <TextInput
                    inputMode="decimal"
                    placeholder={t('inclusions.estimatePlaceholder')}
                    value={inputFromCents(current.estimateCents)}
                    onChange={(e) =>
                      set(row.key, { estimateCents: centsFromInput(e.target.value) })
                    }
                  />
                </label>
              ) : (
                <span className="w-32" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex-1">
          <span className="sr-only">{t('inclusions.addOwn')}</span>
          <TextInput
            placeholder={t('inclusions.addOwn')}
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustom();
              }
            }}
          />
        </label>
        <Button variant="secondary" onClick={addCustom} disabled={customLabel.trim() === ''}>
          {t('inclusions.add')}
        </Button>
      </div>

      <p className="text-caption text-leaf-soft">
        {t('inclusions.extrasTotal', { amount: money(extras, currency) })}
      </p>
    </div>
  );
}
